import { TLBaseShape, BaseBoxShapeUtil, T, RecordProps, BoundsSnapGeometry, HandleSnapGeometry } from "@tldraw/tldraw";

export type LabelUtilType = TLBaseShape<
  "customLabel",
  {
    w: number;
    h: number;
    t: string;
    fill?: string;
    allowUpdate?: boolean;
    colorMix: {
      [key: string]: {
        color: string;
        isHidden: boolean;
      };
    };
    outlineColor?: string;
    dash?: "solid" | "dashed";
    transparency?: number;
    strokeDasharray?: string;
    strokeWidth?: number;
    rounded?: number;
    borderWidth?: number;
    borderColor?: string;
    borderSolid?: "solid" | "dashed";
    borderTopStyle?: string;
    borderBottomStyle?: string;
    borderLeftStyle?: string;
    borderRightStyle?: string;
    isShowBorderTop?: boolean;
    isShowBorderBottom?: boolean;
    isShowBorderLeft?: boolean;
    isShowBorderRight?: boolean;
    filterBlur?: number;
    isCreatedFilterBlur?: boolean;
    filterBlurValueWhenHidden?: number;
    outShadowArray?: Record<
      string,
      { id: string; dx: number; dy: number; shadowBlur: number; color: string; opacity: number; isHidden: boolean }
    >;
    inShadowArray?: Record<
      string,
      { id: string; dx: number; dy: number; shadowBlur: number; color: string; opacity: number; isHidden: boolean }
    >;
    previousBorderColor?: string;
    isCreatedBorderItem?: boolean;
    isHiddenLayerBlur?: boolean;
    isHiddenBorder?: boolean;
    isShowIndicator?: boolean;
  }
>;

export class LabelUtil extends BaseBoxShapeUtil<LabelUtilType> {
  static type = "customLabel" as const;
  static props: RecordProps<LabelUtilType> = {
    w: T.number,
    h: T.number,
    t: T.string,
    fill: T.optional(T.string),
    allowUpdate: T.optional(T.boolean),
    colorMix: T.dict(
      T.string,
      T.object({
        color: T.string,
        isHidden: T.boolean,
      })
    ),
    outlineColor: T.optional(T.string),
    dash: T.optional(T.any),
    transparency: T.optional(T.number),
    strokeDasharray: T.optional(T.string),
    strokeWidth: T.optional(T.number),
    rounded: T.optional(T.number),
    borderWidth: T.optional(T.number),
    borderColor: T.optional(T.string),
    borderSolid: T.optional(T.any),
    borderTopStyle: T.optional(T.string),
    borderBottomStyle: T.optional(T.string),
    borderLeftStyle: T.optional(T.string),
    borderRightStyle: T.optional(T.string),
    isShowBorderTop: T.optional(T.boolean),
    isShowBorderBottom: T.optional(T.boolean),
    isShowBorderLeft: T.optional(T.boolean),
    isShowBorderRight: T.optional(T.boolean),
    filterBlur: T.optional(T.number),
    isCreatedFilterBlur: T.optional(T.boolean),
    filterBlurValueWhenHidden: T.optional(T.number),
    outShadowArray: T.dict(
      T.string,
      T.object({
        id: T.string,
        dx: T.number,
        dy: T.number,
        shadowBlur: T.number,
        color: T.string,
        opacity: T.number,
        isHidden: T.boolean,
      })
    ),
    inShadowArray: T.dict(
      T.string,
      T.object({
        id: T.string,
        dx: T.number,
        dy: T.number,
        shadowBlur: T.number,
        color: T.string,
        opacity: T.number,
        isHidden: T.boolean,
      })
    ),
    previousBorderColor: T.optional(T.string),
    isCreatedBorderItem: T.optional(T.boolean),
    isHiddenLayerBlur: T.optional(T.boolean),
    isHiddenBorder: T.optional(T.boolean),
    isShowIndicator: T.optional(T.boolean),
  };

  getDefaultProps(): LabelUtilType["props"] {
    return {
      w: 100,
      h: 50,
      fill: "red",
      t: "Label",
      allowUpdate: false,
      colorMix: {
        "0": { color: "#e3e3e3", isHidden: false },
      },
      outlineColor: "#000000",
      dash: "solid",
      transparency: 1,
      strokeWidth: 0,
      borderWidth: 0,
      borderColor: "#000000",
      borderSolid: "solid",
      borderTopStyle: "solid",
      borderBottomStyle: "solid",
      borderLeftStyle: "solid",
      borderRightStyle: "solid",
      isShowBorderTop: true,
      isShowBorderBottom: true,
      isShowBorderLeft: true,
      isShowBorderRight: true,
      rounded: 0,
      filterBlur: 0,
      isCreatedFilterBlur: false,
      filterBlurValueWhenHidden: 0,
      outShadowArray: {},
      inShadowArray: {},
      previousBorderColor: "#000000",
      isCreatedBorderItem: false,
      isHiddenLayerBlur: false,
      isHiddenBorder: false,
      isShowIndicator: true,
    };
  }

  component(shape: LabelUtilType) {
    return (
      <div
        style={{
          width: shape.props.w,
          height: shape.props.h,
          color: "black",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 18,
          fontWeight: "bold",
        }}
      >
        {shape.props.t}
      </div>
    );
  }

  indicator(shape: LabelUtilType) {
    return <rect x={0} y={0} width={shape.props.w} height={shape.props.h} />;
  }

  canSnap(_shape: LabelUtilType): boolean {
    return false;
  }

  // snapping
  getBoundsSnapGeometry(_shape: LabelUtilType): BoundsSnapGeometry {
    return { points: [] };
  }

  getHandleSnapGeometry(_shape: LabelUtilType): HandleSnapGeometry {
    return { points: [] };
  }

  canResize(_shape: LabelUtilType): boolean {
    return false;
  }

  hideResizeHandles(_shape: LabelUtilType): boolean {
    return true;
  }

  hideRotateHandle(_shape: LabelUtilType): boolean {
    return true;
  }

  hideSelectionBoundsBg(_shape: LabelUtilType): boolean {
    return true;
  }

  onBeforeUpdate(prev: LabelUtilType, next: LabelUtilType): LabelUtilType | void {
    // Ensure fill property is always defined
    if (next.props.fill === undefined) {
      next.props.fill = prev.props.fill;
    }

    // Allow updates if the flag is set
    if (next.props.allowUpdate) {
      // Reset the flag after the update
      return { ...next, props: { ...next.props, allowUpdate: false, fill: prev.props.fill } };
    }

    // Prevent updates to x and y properties to stop dragging
    if (prev.x !== next.x || prev.y !== next.y) {
      return { ...next, x: prev.x, y: prev.y, props: { ...next.props, fill: prev.props.fill } };
    }

    // Prevent updates to fill and other properties
    const blockedProps = [
      "fill",
      "colorMix",
      "outlineColor",
      "dash",
      "transparency",
      "strokeDasharray",
      "strokeWidth",
      "rounded",
      "borderWidth",
      "borderColor",
      "borderSolid",
      "borderTopStyle",
      "borderBottomStyle",
      "borderLeftStyle",
      "borderRightStyle",
      "isShowBorderTop",
      "isShowBorderBottom",
      "isShowBorderLeft",
      "isShowBorderRight",
      "filterBlur",
      "isCreatedFilterBlur",
      "filterBlurValueWhenHidden",
      "outShadowArray",
      "inShadowArray",
      "previousBorderColor",
      "isCreatedBorderItem",
      "isHiddenLayerBlur",
      "isHiddenBorder",
      "isShowIndicator",
    ];

    const updatedProps = { ...next.props };
    blockedProps.forEach((prop) => {
      // @ts-ignore
      if (prev.props[prop] !== next.props[prop]) {
        // @ts-ignore
        updatedProps[prop] = prev.props[prop];
      }
    });

    // Remove any unexpected properties
    // @ts-ignore
    const { undefined: _, ...cleanedProps } = updatedProps;

    return { ...next, props: cleanedProps };
  }

  onBeforeDelete(shape: LabelUtilType): boolean {
    // Prevent deletion of LabelUtilType shapes
    return false;
  }
}
